import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Chip, Dialog, IconButton, InputAdornment,
    Switch, TablePagination, TextField, Typography, Tooltip,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import GroupIcon from '@material-ui/icons/Group';
import CloseIcon from '@material-ui/icons/Close';
import homeStyles from '../../assets/styles/HomeStyles';
import {
    createPeerService, deletePeerService, editPeerService,
    listPeersService,
} from '../../services/client.service';
import CreatePeerDialog from './components/CreatePeerDialog.comp';
import LoaderWithBackDrop from '../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import { toast } from '../../utils/utils';
import { TOAST_TYPES, USER_ACCESS_LEVEL, DQ_PRO_PLAN_KEY } from '../../utils/constants';
import { useAuth } from '../../context/auth';
import NotFound from '../404/404.page';

const useStyles = makeStyles((theme) => ({
    tableContent: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '20px',
    },
    tableHeader: {
        display: 'grid',
        gridTemplateColumns: '1.5fr 2fr 1fr 1.2fr 1fr 1.5fr',
        padding: '0 20px',
        marginBottom: '12px',
        minWidth: '720px',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { display: 'none' },
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '1.5fr 2fr 1fr 1.2fr 1fr 1.5fr',
        backgroundColor: 'var(--brand-surface, #fff)',
        borderRadius: '16px',
        padding: '14px 20px',
        alignItems: 'center',
        marginBottom: '10px',
        minWidth: '720px',
        border: '1px solid rgba(241, 90, 36, 0.18)',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            padding: '12px 16px',
            gap: 0,
        },
    },
    headerCell: {
        fontSize: '15px !important',
        fontWeight: '700 !important',
        color: 'var(--brand-text, #333)',
        textAlign: 'center',
        [theme.breakpoints.down('md')]: {
            fontSize: '13px !important',
        },
    },
    cellText: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        },
    },
    cellTextLeft: {
        fontSize: '15px !important',
        fontWeight: '500 !important',
        color: 'var(--brand-text, #444)',
        textAlign: 'left',
        wordBreak: 'break-word',
        [theme.breakpoints.down('sm')]: {
            fontSize: '13px !important',
        },
    },
    searchBar: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '50px',
            backgroundColor: 'var(--brand-surface, #fff)',
            '& fieldset': { borderColor: 'rgba(241, 90, 36, 0.4)' },
            '&:hover fieldset': { borderColor: '#FF6900' },
            '&.Mui-focused fieldset': { borderColor: '#FF6900' },
        },
        '& .MuiInputBase-input': {
            padding: '10px 16px',
            fontSize: '14px',
            color: 'var(--brand-text, #333)',
        },
    },
    addBtn: {
        background: '#B7D334 !important',
        color: '#000 !important',
        borderRadius: '50px !important',
        padding: '10px 22px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '14px !important',
        boxShadow: '0 3px 10px rgba(124,181,24,0.25) !important',
        whiteSpace: 'nowrap',
        '&:hover': { background: '#6aa012 !important' },
    },
    editBtn: {
        background: '#6A8EC9 !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '5px 14px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '12px !important',
        boxShadow: 'none !important',
        border: 'none !important',
        '&:hover': { background: '#4f76b8 !important' },
    },
    removeBtn: {
        background: '#C91C6E !important',
        color: '#fff !important',
        borderRadius: '50px !important',
        padding: '5px 14px !important',
        textTransform: 'none !important',
        fontWeight: '700 !important',
        fontSize: '12px !important',
        boxShadow: 'none !important',
        border: 'none !important',
        '&:hover': { background: '#a01558 !important' },
    },
    desktopOnly: { [theme.breakpoints.down('sm')]: { display: 'none !important' } },
    mobilePrimary: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
        },
    },
    mobilePrimaryText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '15px',
            fontWeight: 700,
            color: 'var(--brand-text, #222)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileSubText: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontSize: '12px',
            color: 'var(--brand-text-muted, #666)',
            marginTop: '2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    mobileActions: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            gap: '6px',
            flexShrink: 0,
            alignItems: 'center',
        },
    },
    mobileMetaRow: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            paddingTop: '8px',
            borderTop: '1px solid var(--brand-border, #f0f0f0)',
        },
    },
    mobileMetaTag: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11px',
            color: 'var(--brand-text-muted, #555)',
            background: 'var(--brand-border, #f5f5f5)',
            borderRadius: '20px',
            padding: '2px 8px',
        },
    },
}));

const DQProPeerList = () => {
    const classes = useStyles();
    const homeClasses = homeStyles();
    const { authUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);

    const [peers, setPeers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const debounceRef = useRef(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [peerToDelete, setPeerToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [editOpen, setEditOpen] = useState(false);
    const [peerToEdit, setPeerToEdit] = useState(null);
    const [editFields, setEditFields] = useState({ first_name: '', last_name: '' });
    const [saving, setSaving] = useState(false);
    const [togglingEmail, setTogglingEmail] = useState(null);

    const fetchPeers = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const response = await listPeersService({
                search: opts.search !== undefined ? opts.search : search,
                page:  (opts.page  !== undefined ? opts.page   : page) + 1,
                limit:  opts.limit !== undefined ? opts.limit  : rowsPerPage,
            });
            if (response?.data) {
                setPeers(response.data.peers || []);
                setTotal(response.data.total  || 0);
            }
        } catch (err) {
            console.error('Failed to fetch peers:', err);
        } finally {
            setLoading(false);
        }
    }, [search, page, rowsPerPage]);

    useEffect(() => { fetchPeers(); }, [fetchPeers]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(0);
            fetchPeers({ search: val, page: 0 });
        }, 400);
    };

    const handlePageChange = (_, newPage) => {
        setPage(newPage);
        fetchPeers({ page: newPage });
    };

    const handleRowsPerPageChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        fetchPeers({ limit: newLimit, page: 0 });
    };

    const handleCreatePeer = async (fields) => {
        setCreating(true);
        setLoading(true);
        try {
            const response = await createPeerService(fields);
            if (response?.data?.success) {
                setOpenCreateDialog(false);
                const name = [fields.first_name, fields.last_name].filter(Boolean).join(' ') || fields.email;
                toast(TOAST_TYPES.SUCCESS, 'Peer Added', `Credentials emailed to ${name} (${fields.email})`);
                await fetchPeers();
            } else {
                toast(TOAST_TYPES.DANGER, 'Error', response?.message || 'Failed to add peer');
            }
        } catch {
            // invokeApi already showed the error toast
        } finally {
            setCreating(false);
            setLoading(false);
        }
    };

    const handleToggleStatus = async (peer) => {
        setTogglingEmail(peer.email);
        try {
            const newStatus = !peer.is_active;
            const response = await editPeerService({ email: peer.email, is_active: newStatus });
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Status Updated',
                    `${peer.email} is now ${newStatus ? 'Active' : 'Inactive'}`);
                await fetchPeers();
            } else {
                toast(TOAST_TYPES.DANGER, 'Error', response?.message || 'Failed to update status');
            }
        } catch {
            toast(TOAST_TYPES.DANGER, 'Error', 'Failed to update status');
        } finally {
            setTogglingEmail(null);
        }
    };

    const openEdit = (peer) => {
        const parts = (peer.name || '').split(' ');
        setPeerToEdit(peer);
        setEditFields({
            first_name: parts[0] || '',
            last_name:  parts.slice(1).join(' ') || '',
        });
        setEditOpen(true);
    };

    const handleEditSave = async () => {
        setSaving(true);
        try {
            const response = await editPeerService({ email: peerToEdit.email, ...editFields });
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Updated', `${peerToEdit.email} updated`);
                setEditOpen(false);
                setPeerToEdit(null);
                await fetchPeers();
            } else {
                toast(TOAST_TYPES.DANGER, 'Error', response?.message || 'Failed to update peer');
            }
        } catch {
            toast(TOAST_TYPES.DANGER, 'Error', 'Failed to update peer');
        } finally {
            setSaving(false);
        }
    };

    const openDeleteConfirm = (peer) => {
        setPeerToDelete(peer);
        setConfirmOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        if (!peerToDelete) return;
        setDeleting(true);
        try {
            const response = await deletePeerService(peerToDelete.email);
            if (response?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Peer Removed', `${peerToDelete.email} removed`);
                setConfirmOpen(false);
                setPeerToDelete(null);
                await fetchPeers();
            } else {
                toast(TOAST_TYPES.DANGER, 'Error', response?.message || 'Failed to remove peer');
            }
        } catch {
            toast(TOAST_TYPES.DANGER, 'Error', 'Failed to remove peer');
        } finally {
            setDeleting(false);
        }
    };

    const isPaidUser = authUser?.dq_pro_plan?.status === 'active' &&
        authUser?.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT &&
        authUser?.access_level !== USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE;
    if (!isPaidUser) return <NotFound />;

    return (
        <Box style={{ paddingTop: '18px' }}>
            <LoaderWithBackDrop loading={loading} />

            <Typography style={{ fontSize: 35, fontWeight: 800, marginBottom: 24, textAlign: 'center', color: 'var(--brand-text, #111)' }}>
                Peers
            </Typography>

            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <TextField
                    size="small"
                    placeholder="Search by name, email…"
                    value={search}
                    onChange={handleSearchChange}
                    className={classes.searchBar}
                    style={{ width: 320 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <SearchIcon style={{ color: '#FF6900' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button className={classes.addBtn} disabled={loading} onClick={() => setOpenCreateDialog(true)}>
                    + Add Peer
                </Button>
            </Box>

            <Box className={classes.tableContent}>
                {peers.length > 0 && (
                    <Box className={classes.tableHeader}>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Name</Typography>
                        <Typography className={classes.headerCell} style={{ textAlign: 'left' }}>Email</Typography>
                        <Typography className={classes.headerCell}>Status</Typography>
                        <Typography className={classes.headerCell}>Added On</Typography>
                        <Typography className={classes.headerCell}>Actions</Typography>
                    </Box>
                )}

                {peers.length > 0 ? peers.map((peer, idx) => (
                    <Box key={peer.email + idx} className={classes.row}>
                        {/* Desktop cells */}
                        <Typography className={`${classes.cellTextLeft} ${classes.desktopOnly}`}>{peer.name || '—'}</Typography>
                        <Typography className={`${classes.cellTextLeft} ${classes.desktopOnly}`}>{peer.email}</Typography>
                        <Box className={classes.desktopOnly} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
                            <Tooltip title={peer.is_active ? 'Click to deactivate' : 'Click to activate'}>
                                <Switch
                                    size="small"
                                    checked={!!peer.is_active}
                                    disabled={togglingEmail === peer.email}
                                    onChange={() => handleToggleStatus(peer)}
                                    color="success"
                                />
                            </Tooltip>
                            <Typography style={{ fontSize: 12, color: peer.is_active ? 'green' : '#aaa' }}>
                                {peer.is_active ? 'Active' : 'Inactive'}
                            </Typography>
                        </Box>
                        <Typography className={`${classes.cellText} ${classes.desktopOnly}`}>
                            {peer.created_at ? new Date(peer.created_at).toLocaleDateString() : '—'}
                        </Typography>
                        <Box className={classes.desktopOnly} style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                            <Button className={classes.editBtn} size="small" onClick={() => openEdit(peer)}>
                                Edit
                            </Button>
                            <Button className={classes.removeBtn} size="small" onClick={() => openDeleteConfirm(peer)}>
                                Remove
                            </Button>
                        </Box>
                        {/* Mobile card */}
                        <Box className={classes.mobilePrimary}>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                                <Typography className={classes.mobilePrimaryText}>{peer.name || '—'}</Typography>
                                <Typography className={classes.mobileSubText}>{peer.email}</Typography>
                            </Box>
                            <Box className={classes.mobileActions}>
                                <Button className={classes.editBtn} size="small" onClick={() => openEdit(peer)}>Edit</Button>
                                <Button className={classes.removeBtn} size="small" onClick={() => openDeleteConfirm(peer)}>Remove</Button>
                            </Box>
                        </Box>
                        <Box className={classes.mobileMetaRow}>
                            <span className={classes.mobileMetaTag} style={{ color: peer.is_active ? 'green' : '#aaa' }}>
                                {peer.is_active ? '● Active' : '● Inactive'}
                            </span>
                            {peer.created_at && (
                                <span className={classes.mobileMetaTag}>{new Date(peer.created_at).toLocaleDateString()}</span>
                            )}
                        </Box>
                    </Box>
                )) : !loading && (
                    <Box style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                        backgroundColor: 'var(--brand-surface, #fff)',
                        borderRadius: '16px',
                        border: '1px dashed rgba(241, 90, 36, 0.3)',
                        minWidth: '720px',
                    }}>
                        <GroupIcon style={{ fontSize: 56, color: '#FF6900', opacity: 0.35, marginBottom: 16 }} />
                        <Typography style={{ fontSize: 18, fontWeight: 700, color: 'var(--brand-text, #333)', marginBottom: 8 }}>
                            {search ? `No peers found for "${search}"` : 'No peers added yet'}
                        </Typography>
                        <Typography style={{ fontSize: 14, color: 'var(--brand-text-muted, #888)', textAlign: 'center', maxWidth: 380 }}>
                            {search ? 'Try a different name or email.' : 'A peer is a colleague you invite to share your workspace — they get their own login and can manage the same programs, classes and clients as you. Add one to run the portal together.'}
                        </Typography>
                    </Box>
                )}

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            </Box>

            <CreatePeerDialog
                open={openCreateDialog}
                onClose={() => setOpenCreateDialog(false)}
                onSubmit={handleCreatePeer}
                loading={creating}
            />

            <Dialog
                open={editOpen}
                onClose={() => !saving && setEditOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '15px',
                        overflow: { xs: 'scroll', sm: 'hidden' },
                    }
                }}
            >
                <Box sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => !saving && setEditOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h5" sx={{ mt: 2, mb: 4, textAlign: 'center', color: '#ff5757', fontWeight: 'bold' }}>
                        Edit Peer
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField label="Email" value={peerToEdit?.email || ''} disabled fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="First Name"
                                value={editFields.first_name}
                                onChange={(e) => { const v = e.target.value; setEditFields(f => ({ ...f, first_name: v })); }}
                                fullWidth
                            />
                            <TextField
                                label="Last Name"
                                value={editFields.last_name}
                                onChange={(e) => { const v = e.target.value; setEditFields(f => ({ ...f, last_name: v })); }}
                                fullWidth
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                        <Button
                            className={homeClasses.popupButton}
                            variant="outlined"
                            disabled={saving}
                            onClick={() => setEditOpen(false)}
                            sx={{ width: '30%', borderColor: '#ff5757', color: '#ff5757', fontWeight: 'bold', fontSize: '15px !important' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={homeClasses.popupButton}
                            variant="contained"
                            disabled={saving}
                            onClick={handleEditSave}
                            sx={{ width: '30%', fontSize: '15px !important' }}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={() => !deleting && setConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '15px',
                        overflow: { xs: 'scroll', sm: 'hidden' },
                    }
                }}
            >
                <Box sx={{ p: 4, position: 'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => !deleting && setConfirmOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, color: '#999' }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h5" sx={{ mt: 2, mb: 4, textAlign: 'center', color: '#ff5757', fontWeight: 'bold' }}>
                        Remove Peer
                    </Typography>

                    <Typography sx={{ textAlign: 'center', color: 'var(--brand-text, #374151)', fontSize: '15px' }}>
                        Remove <strong>{peerToDelete?.email}</strong>? They will immediately lose portal access.
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                        <Button
                            className={homeClasses.popupButton}
                            variant="outlined"
                            disabled={deleting}
                            onClick={() => setConfirmOpen(false)}
                            sx={{ width: '40%', borderColor: '#ff5757', color: '#ff5757', fontWeight: 'bold', fontSize: '15px !important' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={homeClasses.popupButton}
                            variant="contained"
                            disabled={deleting}
                            onClick={handleDeleteConfirmed}
                            sx={{ width: '40%', fontSize: '15px !important' }}
                        >
                            {deleting ? 'Removing...' : 'Remove'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default DQProPeerList;
